/**
 * Customer authentication context (separate from admin).
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "./providers";

const Ctx = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/customer/me`, { withCredentials: true });
      setUser(data);
      localStorage.setItem("dzts_has_session", "1");
    } catch {
      setUser(null);
      localStorage.removeItem("dzts_has_session");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback page will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip the network call (and its 401 console noise) for visitors who never logged in
    if (!localStorage.getItem("dzts_has_session")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/customer/login`, { email, password }, { withCredentials: true });
    setUser(data.user);
    localStorage.setItem("dzts_has_session", "1");
    return data.user;
  };

  const register = async (payload) => {
    // Backend now returns {ok, pending, email} — user must confirm via email before login
    const { data } = await axios.post(`${API}/customer/register`, payload);
    return data;  // { ok, pending, email }
  };

  const forgotPassword = async (email, lang = "fr") => {
    const { data } = await axios.post(`${API}/customer/forgot-password`, { email, lang });
    return data;
  };

  const resendConfirmation = async (email, lang = "fr") => {
    const { data } = await axios.post(`${API}/customer/resend-confirmation`, { email, lang });
    return data;
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/mon-compte";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeGoogle = async (sessionId) => {
    const { data } = await axios.post(
      `${API}/customer/google-callback`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(data.user);
    localStorage.setItem("dzts_has_session", "1");
    return data.user;
  };

  const logout = async () => {
    await axios.post(`${API}/customer/logout`, {}, { withCredentials: true });
    setUser(null);
    localStorage.removeItem("dzts_has_session");
  };

  return (
    <Ctx.Provider value={{ user, loading, login, register, forgotPassword, resendConfirmation, googleSignIn, exchangeGoogle, logout, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useCustomer() {
  return useContext(Ctx) || { user: null, loading: false };
}
